import { webStorage } from 'sidecar-utility-library'
import { datadogLogs } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum'
import { logError } from 'utils/monitoring/datadog'
import {
  trackRecoveryAttempt,
  trackRecoveryOutcome,
  trackOpenSuppressed,
} from 'utils/chat/chatRecoveryTelemetry'

/**
 * This Hook has all the functionality that we'll need to manage the chat widget
 * on the web app.
 *
 * @returns Functions to control the chat widget
 */
export const useChatWidget = () => {
  return {
    setSubscribers,
    setChatAttributes,
    addChatAttributes,
    clearChatAttributes,
    openChatWithRecovery,
  }
}

/**
 * We use this to check if the user is logged in. We don't want to to clear the
 * conversations of someone who is logged in. This may not be the best way to do
 * this, but it works now.
 */
const hasToken = () => webStorage?.getItem('token')

/**
 * We set up a bunch of global vars to track when various events happen so that
 * we can build logic around things like keeping personal info private. To try
 * and keep the global space clear, we're going to use a namespaced object.
 */
const chat = {}
window.chat = chat
chat.databaseReady = false
chat.subscribersSet = false
chat.snippetMounted = false
chat.customAttrs = {}
chat.isMessengerOpen = false
chat.messengerStateListeners = []

let recoveryInFlight = false

/**
 * The methods below are used by the above hook but can also be called directly
 * because in some cases we need to call them from middleware or places other
 * than where a hook can be used. The fact that they rely on global values makes
 * this possible.
 */

/**
 * We use a global variable to track that we've started to mount the chat
 * widget. This is because the widget must only be mounted once, and it will
 * throw errors if mounted multiple times. Using a global helps stop things
 * immediately once mounting begins.
 *
 * @param {string} type What type of widget to init with, 'guest' is safest
 */
export const setSubscribers = () => {
  // only mount the snippet if we haven't already
  if (!chat.snippetMounted) {
    chat.snippetMounted = true

    // just because the snippet is mounted, doesn't mean the widget is ready
    // so this interval will check that the widget is loaded, then subscribe
    // to it's 'ready' event so that we know both the snippet is mounted and
    // the launcher is ready to be used. The interval is relatively short so
    // that we detect changes ASAP.
    const gCheck = setInterval(() => {
      // using a global to make sure to not create multiple subscribers
      if (!chat.subscribersSet && window.Genesys) {
        // as soon as the launcher is ready we can start setting attributes and
        // getting things set up for chats to happen.
        window.Genesys('subscribe', 'Launcher.ready', () => {
          clearInterval(gCheck)
        })

        // when the messenger is opened, track the state and notify listeners
        window.Genesys('subscribe', 'Messenger.opened', () => {
          // Check if this opened event was expected (following Messenger.open call)
          const wasExpected =
            chat.expectingMessengerOpen && chat.expectingMessengerOpen()
          const isUnsolicited = !wasExpected

          if (isUnsolicited) {
            // This opened event fired WITHOUT a corresponding Messenger.open call
            // This is the suspected ghost chat trigger
            datadogLogs.logger.warn('Unsolicited Messenger.opened detected', {
              currentPath: window.location.pathname,
              visibilityState: document.visibilityState,
              memberUuid: chat.customAttrs?.memberUuid,
              hasActiveSession: chat.messagingServiceStarted,
              wasMessengerAlreadyOpen: chat.isMessengerOpen,
            })
          }

          // Clear expectation flag if it was set
          if (chat.clearMessengerOpenExpectation) {
            chat.clearMessengerOpenExpectation()
          }

          chat.isMessengerOpen = true
          chat.messengerStateListeners.forEach(listener => listener(true))

          // Track when messenger was last opened for timing analysis
          chat.lastMessengerOpenedTime = Date.now()

          datadogLogs.logger.info('Genesys messenger opened', {
            currentPath: window.location.pathname,
            visibilityState: document.visibilityState,
            memberUuid: chat.customAttrs?.memberUuid,
            wasExpected: wasExpected,
            isUnsolicited: isUnsolicited,
          })
        })

        // when the messenger is closed hide the launcher so it doesn't
        // show in the lower right corner, and notify listeners
        window.Genesys('subscribe', 'Messenger.closed', () => {
          chat.isMessengerOpen = false
          chat.messengerStateListeners.forEach(listener => listener(false))
          datadogLogs.logger.info('Genesys messenger closed', {
            currentPath: window.location.pathname,
            visibilityState: document.visibilityState,
            memberUuid: chat.customAttrs?.memberUuid,
          })
          window.Genesys('command', 'Launcher.hide')
        })

        // when the messenger is started, that means that either the user has
        // clicked on the button to start the session or that the session was
        // restored from a previous conversation. This event includes a response
        // that has info about the session that we can combine with token checks
        // to determine whether or not we should clear the conversation.
        window.Genesys('subscribe', 'MessagingService.started', response => {
          chat.messagingServiceStarted = true
          const isNewSession = response.data.newSession
          const isAuthenticated = response.data.authenticated
          const isReadOnly = response.data.readOnly

          // Calculate time since last messenger opened event
          const timeSinceLastOpened = chat.lastMessengerOpenedTime
            ? Date.now() - chat.lastMessengerOpenedTime
            : null

          datadogLogs.logger.info('Genesys session started', {
            newSession: isNewSession,
            authenticated: isAuthenticated,
            readOnly: isReadOnly,
            hasToken: !!hasToken(),
            currentPath: window.location.pathname,
            visibilityState: document.visibilityState,
            memberUuid: chat.customAttrs?.memberUuid,
            millisecondsSinceMessengerOpened: timeSinceLastOpened,
          })

          // guest users should always have their conversations cleared on load
          // since we cannot guarantee that it's the same guest as before. We expect
          // that members will at least log out which clears it that way anyway.
          if (!hasToken() && !isNewSession) {
            datadogLogs.logger.info(
              'Clearing guest conversation (restored old session)',
              {
                currentPath: window.location.pathname,
              },
            )
            window.Genesys('command', 'MessagingService.clearConversation')
          }
        })

        // when a conversation is cleared, reset the started flag so that the
        // logout page cleanup knows whether it needs to clear the conversation
        window.Genesys(
          'subscribe',
          'MessagingService.conversationCleared',
          () => {
            chat.messagingServiceStarted = false
            datadogLogs.logger.info('Genesys conversation cleared', {
              currentPath: window.location.pathname,
              visibilityState: document.visibilityState,
              memberUuid: chat.customAttrs?.memberUuid,
            })
          },
        )

        // log when conversations are restored from localStorage so we can
        // track down ghost chat issues
        window.Genesys('subscribe', 'MessagingService.restored', response => {
          // Calculate time since last messenger opened event
          const timeSinceLastOpened = chat.lastMessengerOpenedTime
            ? Date.now() - chat.lastMessengerOpenedTime
            : null

          datadogLogs.logger.info('Genesys conversation restored', {
            messageCount: response.data?.messages?.length,
            hasToken: !!hasToken(),
            currentPath: window.location.pathname,
            visibilityState: document.visibilityState,
            memberUuid: chat.customAttrs?.memberUuid,
            millisecondsSinceMessengerOpened: timeSinceLastOpened,
          })
        })

        // "Database.ready" means that the database plugin is ready to be used.
        // We rely on this to know that we can start setting attributes.
        window.Genesys('subscribe', 'Database.ready', () => {
          chat.databaseReady = true
        })

        // right now this is just for logging what we've set in the database
        // we can probably get rid of it after the widget is working well.
        /* window.Genesys('subscribe', 'Database.updated', ({ data }) => {
          //TODO: commenting this out for now because it's too noisy
          // Can probably get rid of this alltogehter once we're sure
          // the widget is working well.
          // console.log(
          //   '%cDATABASE updated',
          //   'color: cornflowerblue',
          //   data.messaging.customAttributes,
          // )
        }) */

        // Track if we're expecting a Messenger.opened event following a user action
        let expectingMessengerOpen = false
        let messengerOpenTimeout = null

        // Wrap Genesys to log all Messenger.open calls and track expectations
        const originalGenesys = window.Genesys
        window.Genesys = function (...args) {
          if (args[0] === 'command' && args[1] === 'Messenger.open') {
            // User or code explicitly opened messenger - we expect opened event
            expectingMessengerOpen = true
            clearTimeout(messengerOpenTimeout)
            messengerOpenTimeout = setTimeout(() => {
              expectingMessengerOpen = false
            }, 5000) // 5 second window for opened event to fire

            // Capture stack trace to see what's calling Messenger.open
            const stackTrace = new Error().stack
            const callerLine = stackTrace
              ?.split('\n')[2]
              ?.trim()
              ?.replace(/^at\s+/, '')

            datadogLogs.logger.info('Messenger.open called', {
              currentPath: window.location.pathname,
              messengerAlreadyOpen: chat.isMessengerOpen,
              hasActiveSession: chat.messagingServiceStarted,
              memberUuid: chat.customAttrs?.memberUuid,
              caller: callerLine,
              visibilityState: document.visibilityState,
            })
          }
          return originalGenesys.apply(this, args)
        }

        // Store the expectation clearing function so opened handler can access it
        chat.expectingMessengerOpen = () => expectingMessengerOpen
        chat.clearMessengerOpenExpectation = () => {
          expectingMessengerOpen = false
          clearTimeout(messengerOpenTimeout)
        }

        // this variable prevents us from setting the subscribers more than once.
        // like listners, too many can cause compounded/exponential problems.
        chat.subscribersSet = true
      }
    }, 500)

    // Track tab visibility changes to correlate with ghost chat occurrences
    const visibilityHandler = () => {
      if (
        document.visibilityState === 'visible' &&
        chat.messagingServiceStarted
      ) {
        datadogLogs.logger.info('Tab became visible with active chat', {
          currentPath: window.location.pathname,
          messengerOpen: chat.isMessengerOpen,
          memberUuid: chat.customAttrs?.memberUuid,
        })
      }
    }
    document.addEventListener('visibilitychange', visibilityHandler)

    // Store handler for potential cleanup (though this runs once)
    chat.visibilityHandler = visibilityHandler
  }
}

/**
 * Sets attributes for the chat widget in the hopes that agents are able to
 * glean information about who the member is without having to ask them. Because
 * we must set all the values together (or else we risk losing values), we'll
 * store them in a global object and then send them all at once.
 *
 * TODO: this runs a lot, how can we make it run less? Maybe check the content
 * and exit (return) if value and attr already exist?
 *
 * @param {object} attrs An object containing a set of key/value pairs to pass
 *   to Genesys
 */
export const setChatAttributes = (newAttrs = {}) => {
  chat.customAttrs = {
    ...newAttrs,
  }

  // the productType tells us which skill to use so when we set it
  // we should also set the skill attribute. Doing this here simplifies
  // things by removing all the skill attr setting from everywhere in the
  // code and making it based on something more concrete
  if ('productType' in newAttrs) {
    switch (newAttrs.productType) {
      case 'ASO':
      case 'GFI':
        chat.customAttrs.skill = 'group'
        break

      default:
        chat.customAttrs.skill = 'individual'
    }
  }

  // don't bother doing anything if it's not loaded yet. In most cases
  // this will be run multiple times so eventually all the attributes will
  // get set.
  if (!window.Genesys) {
    return
  }

  // This conditional is here to make it so we can flexibly update the DB
  // If it's already ready, this variable will be set to true and we can just
  // go ahead and call the update command. If it's not ready, we'll subscribe
  // to the ready event and then call the update command.
  if (chat.databaseReady) {
    window.Genesys(
      'command',
      'Database.update',
      {
        messaging: {
          customAttributes: chat.customAttrs,
        },
      },
      null,
      () => {
        logError('Failed to update custom attributes for Genesys', {
          context: 'member-comms-genesys',
          databaseReady: true,
        })
      },
    )
  } else {
    window.Genesys('subscribe', 'Database.ready', () => {
      window.Genesys(
        'command',
        'Database.update',
        { messaging: { customAttributes: chat.customAttrs } },
        null,
        () => {
          logError('Failed to update custom attributes for Genesys', {
            context: 'member-comms-genesys',
            databaseReady: false,
          })
        },
      )
    })
  }
}

/**
 * Adds custom attributes to the chat widget without disturbing the ones already
 * set.
 *
 * `setChatAttributes` above deliberately _replaces_ `chat.customAttrs`
 * wholesale — it is called from `memberMiddleware` on every member fetch with
 * the full member identity, and replacing is how a stale `companyName` or
 * `productType` from a previous policy stops being sent. Calling it to add one
 * key would wipe the member's name, DOB, uuid, and the `skill` that routes them
 * to the right queue.
 *
 * So: a separate door for callers that own one attribute and know nothing about
 * the rest. The Ash hand-off is the first (see `SidekickProvider`'s
 * `OPEN_MEMBER_CARE_CHAT`), which fires on a click, long after the middleware
 * has populated identity.
 *
 * Note the ordering consequence: because `setChatAttributes` replaces, a member
 * fetch that lands _after_ this call drops what this added. That is the right
 * way round for the hand-off — the attributes describe one Genesys conversation
 * the member is opening now, and a policy refresh mid-conversation is rare —
 * but a caller that needs its keys to outlive a refetch needs a different
 * mechanism, not this one.
 *
 * The write is not always synchronous. When the Database plugin hasn't
 * announced itself yet this subscribes to `Database.ready` and returns, so
 * callers cannot assume the attributes have reached Genesys by the time this
 * returns — a `Messenger.open` right after can win the race on a cold start.
 * Calling this first is still correct (it's the only ordering we control), but
 * the guarantee is "as early as possible", not "before the conversation
 * opens".
 *
 * @param {object} extraAttrs Key/value pairs to add to what's already set.
 */
export const addChatAttributes = (extraAttrs = {}) => {
  chat.customAttrs = {
    ...chat.customAttrs,
    ...extraAttrs,
  }

  if (!window.Genesys) {
    return
  }

  // Same ready/not-ready split as `setChatAttributes`, and for the same reason:
  // the Database plugin rejects writes before it announces itself, and the
  // hand-off's click can easily be the first thing that touches it.
  const update = () => {
    window.Genesys(
      'command',
      'Database.update',
      { messaging: { customAttributes: chat.customAttrs } },
      null,
      () => {
        logError('Failed to add custom attributes for Genesys', {
          context: 'member-comms-genesys',
          attrs: Object.keys(extraAttrs).join(','),
        })
      },
    )
  }

  if (chat.databaseReady) {
    update()
  } else {
    window.Genesys('subscribe', 'Database.ready', update)
  }
}

/**
 * Removes all the custom attributes from the global namespace for situations
 * like a member logging out. This really only runs on logout as that's when we
 * actually need to reset these.
 */
export function clearChatAttributes() {
  // we clear the attrs here, but they don't actually get removed from the
  // database plugin. That's generally ok because in a few blinks, we'll be
  // sending new ones and that'll update the db for us.
  Object.keys(chat.customAttrs).forEach(key => {
    delete chat.customAttrs[key]
  })
}

/**
 * ASH-887: Messenger.open can no-op when Genesys's own webmessaging session is
 * stale (session.not.found) and never surfaces an error — the incident that
 * prompted this showed zero Genesys API calls at all, so detection has to rely
 * on a timeout (no Messenger.opened), not MessagingService.error.
 *
 * Stage order: bare retry first (protects an active-but-slow session from being
 * wiped by clearConversation), then clearConversation + retry, then give up and
 * call onPermanentFailure. `attempt` invalidates stale listeners from earlier
 * stages, since window.Genesys has no unsubscribe.
 *
 * @param {object} options
 * @param {string} [options.sessionId] - Ash session id, echoed back in the
 *   MEMBER_CARE_CHAT_FAILED signal so Ash can match it to the right card.
 * @param {function} options.onPermanentFailure - Called with sessionId once all
 *   recovery attempts are exhausted.
 * @param {number} [options.timeoutMs] - Time to wait for Messenger.opened
 *   before advancing to the next recovery stage.
 */
export function openChatWithRecovery({
  sessionId,
  onPermanentFailure,
  timeoutMs = 4000,
}) {
  if (!window.Genesys) {
    logError('Genesys chat handoff failed permanently', {
      source: 'genesys-chat-recovery',
      sessionId,
      stage: 'initial',
      reason: 'genesys-unavailable',
      attempts: 0,
    })
    datadogRum.addError(new Error('Genesys chat handoff failed permanently'), {
      source: 'genesys-chat-recovery',
      sessionId,
      stage: 'initial',
      reason: 'genesys-unavailable',
      attempts: 0,
    })
    trackRecoveryOutcome({
      outcome: 'failed',
      stage: 'initial',
      reason: 'genesys-unavailable',
      openAttempts: 0,
      sessionId,
    })
    onPermanentFailure(sessionId)
    return
  }

  if (recoveryInFlight) {
    trackOpenSuppressed({ sessionId })
    return
  }

  const stages = ['initial', 'bare-retry', 'clear-retry']
  let attempt = 0
  let settled = false
  const finishRecovery = () => {
    recoveryInFlight = false
  }

  const runStage = stageIndex => {
    attempt += 1
    const attemptId = attempt
    const isCurrent = () => !settled && attemptId === attempt
    let timeoutId

    trackRecoveryAttempt({
      stage: stages[stageIndex],
      attempt: stageIndex + 1,
      sessionId,
    })

    const succeed = () => {
      if (!isCurrent()) return
      settled = true
      clearTimeout(timeoutId)
      trackRecoveryOutcome({
        outcome: 'recovered',
        stage: stages[stageIndex],
        openAttempts: stageIndex + 1,
        sessionId,
      })
      finishRecovery()
    }

    const fail = reason => {
      if (!isCurrent()) return
      clearTimeout(timeoutId)

      const nextIndex = stageIndex + 1
      if (nextIndex >= stages.length) {
        settled = true
        logError('Genesys chat handoff failed permanently', {
          source: 'genesys-chat-recovery',
          sessionId,
          stage: stages[stageIndex],
          reason,
          attempts: stageIndex + 1,
        })
        datadogRum.addError(
          new Error('Genesys chat handoff failed permanently'),
          {
            source: 'genesys-chat-recovery',
            sessionId,
            stage: stages[stageIndex],
            reason,
            attempts: stageIndex + 1,
          },
        )
        trackRecoveryOutcome({
          outcome: 'failed',
          stage: stages[stageIndex],
          reason,
          openAttempts: stageIndex + 1,
          sessionId,
        })
        try {
          onPermanentFailure(sessionId)
        } finally {
          finishRecovery()
        }
        return
      }

      if (stages[nextIndex] === 'clear-retry') {
        // Invalidate this failed attempt's listeners now, not just when
        // runStage(nextIndex) eventually runs — otherwise a late
        // Messenger.opened for the attempt we're abandoning can still pass
        // isCurrent() during the async clearConversation wait below and
        // settle recovery without ever reaching the clear-retry stage.
        attempt += 1
        let clearedHandled = false
        const proceedToClearRetry = () => {
          if (clearedHandled || settled) return
          clearedHandled = true
          // Re-send the last known attributes — clearConversation can drop
          // them from the database plugin.
          setChatAttributes(chat.customAttrs)
          runStage(nextIndex)
        }

        window.Genesys('command', 'MessagingService.clearConversation')
        window.Genesys(
          'subscribe',
          'MessagingService.conversationCleared',
          proceedToClearRetry,
        )
        // Fallback in case conversationCleared never fires.
        setTimeout(proceedToClearRetry, 1000)
      } else {
        runStage(nextIndex)
      }
    }

    window.Genesys('subscribe', 'Messenger.opened', succeed)
    window.Genesys('subscribe', 'MessagingService.error', () => fail('error'))
    window.Genesys('command', 'Messenger.open')

    timeoutId = setTimeout(() => fail('timeout'), timeoutMs)
  }

  recoveryInFlight = true
  runStage(0)
}

/**
 * Subscribe to Genesys Messenger open/close state changes. Used by
 * SidekickProvider to reposition the poptart when Genesys is open.
 *
 * @param {function} listener - Callback function that receives isOpen boolean
 * @returns {function} Unsubscribe function to remove the listener
 */
export function subscribeToMessengerState(listener) {
  chat.messengerStateListeners.push(listener)

  // Return current state immediately if messenger is already open
  if (chat.isMessengerOpen) {
    listener(true)
  }

  // Return unsubscribe function
  return () => {
    chat.messengerStateListeners = chat.messengerStateListeners.filter(
      l => l !== listener,
    )
  }
}
